import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api, type Me } from "./api";

/** Why the last session ended on its own (SQ2.0 switched the login off, closed this web, or reset it to a
 *  first password) — the login form says it once. */
export type Ended = { code: string; error?: string; sq?: string[] } | null;

type Ctx = {
  user: Me | null;
  loading: boolean;
  ended: Ended;
  login: (u: string, p: string) => Promise<void>;
  loginFirst: (b: { username: string; password: string; next: string; confirm: string }) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};
const AuthCtx = createContext<Ctx>({} as Ctx);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [ended, setEnded] = useState<Ended>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.me();
      setUser(r.user);
      if (!r.user && r.code) setEnded({ code: r.code, error: r.error, sq: r.sq });
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(
    async (u: string, p: string) => {
      await api.login(u, p);
      setEnded(null);
      await refresh();
    },
    [refresh],
  );
  const loginFirst = useCallback(
    async (b: { username: string; password: string; next: string; confirm: string }) => {
      await api.loginFirst(b);
      setEnded(null);
      await refresh();
    },
    [refresh],
  );
  const logout = useCallback(async () => {
    await api.logout();
    setUser(null);
  }, []);

  return <AuthCtx.Provider value={{ user, loading, ended, login, loginFirst, logout, refresh }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
